import { useMemo } from 'react'
import { Alert, Box, Typography } from '@mui/material'
import { Check, TriangleAlert } from 'lucide-react'
import type { Run } from '../../api/types'
import { EudrTag, Eyebrow, RepairableTag, StackedBar } from '../../components/primitives'
import { formatDate, formatTime, num, pct } from '../../lib/format'
import { tokens } from '../../theme'
import { byPanelOrder } from '../newrun/checkMeta'

/**
 * The run's standing, and its checks as filters.
 *
 * Shared by the map and the table so switching between them keeps the run in
 * view: the global rail belongs to the app, this panel belongs to the run.
 */
export function RunSummaryPanel({
  run, activeChecks, onToggleCheck,
}: { run: Run; activeChecks: string[]; onToggleCheck: (key: string) => void }) {
  const summary = run.summary
  const checks = useMemo(
    () => Object.values(summary?.checks ?? {}).filter((c) => c.enabled).sort(byPanelOrder),
    [summary],
  )
  const compliance = checks.filter((c) => c.tier === 'compliance')
  const quality = checks.filter((c) => c.tier === 'data_quality')
  /** Enabled, attempted, and unable to reach a verdict. */
  const skipped = checks.filter((c) => c.error)
  /** Ran, but some polygons were outside its reference layer and were not judged. */
  const partial = checks.filter((c) => !c.error && Number(c.meta?.not_covered ?? 0) > 0)
  const t = summary?.totals

  return (
    <Box
      component="aside"
      sx={{
        width: 340, flex: 'none', backgroundColor: 'background.paper',
        borderRight: `1px solid ${tokens.color.ink200}`, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}
    >
      <Box sx={{ p: 5, borderBottom: `1px solid ${tokens.color.ink200}` }}>
        <Eyebrow sx={{ mb: 2 }}>
          Run · {formatDate(run.finished_at ?? run.created_at)}, {formatTime(run.finished_at ?? run.created_at)}
        </Eyebrow>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography
            sx={{
              font: `700 40px/1 ${tokens.font.display}`, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums', color: tokens.color.brandDeep,
            }}
            data-testid="compliant-pct"
          >
            {pct(t?.compliant_pct, 1)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>compliant</Typography>
        </Box>
        <Box sx={{ my: 3.5 }}>
          <StackedBar
            passed={t?.passed ?? 0}
            flagged={t?.flagged ?? 0}
            rejected={t?.rejected ?? 0}
            excluded={t?.excluded ?? 0}
            total={t?.plots ?? 0}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          <Stat testId="total-polygons" value={t?.plots ?? 0} label="polygons" />
          <Stat testId="total-rejected" value={t?.rejected ?? 0} label="rejected" colour={tokens.color.danger} />
          <Stat testId="total-flagged" value={t?.flagged ?? 0} label="flagged" colour={tokens.color.warning} />
        </Box>
      </Box>

      {skipped.length > 0 && (
        // The compliant figure above counts a plot as compliant when nothing
        // flagged it — including when the thing that would have flagged it
        // never ran. Saying so beside the figure is the least this can do.
        <Alert severity="warning" variant="outlined" sx={{ mx: 5, mt: 3 }}>
          {skipped.length === 1
            ? `${skipped[0]!.label} did not run, so these polygons were not judged against it.`
            : `${skipped.length} checks did not run, so these polygons were not judged against them.`}
        </Alert>
      )}
      {partial.length > 0 && (
        // Same reasoning: a polygon outside the land-use or forest layers
        // was not judged against them, and still counts as compliant above.
        <Alert severity="warning" variant="outlined" sx={{ mx: 5, mt: 3 }}>
          {partial.map((c) => `${num(Number(c.meta!.not_covered))} polygons are outside ${c.label}'s reference layer`).join('; ')}
          {' '}and were not judged against it.
        </Alert>
      )}

      <Box sx={{ px: 5, pt: 3.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="overline" sx={{ color: tokens.color.danger }}>Compliance · reject</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>polygons</Typography>
      </Box>
      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column' }}>
        {compliance.map((c) => (
          <CheckRow
            key={c.key}
            label={c.label}
            count={c.plots}
            error={c.error}
            notCovered={Number(c.meta?.not_covered ?? 0)}
            tone="high"
            eudr={c.eudr}
            active={activeChecks.includes(c.key)}
            onClick={() => onToggleCheck(c.key)}
          />
        ))}
      </Box>

      <Box sx={{ px: 5, pt: 3.5, pb: 1.5, mt: 2.5, borderTop: `1px solid ${tokens.color.ink200}` }}>
        <Typography variant="overline" sx={{ color: tokens.color.warning }}>Data quality · flag</Typography>
      </Box>
      <Box sx={{ px: 2, pb: 4, display: 'flex', flexDirection: 'column' }}>
        {quality.map((c) =>
          c.scope === 'region' ? (
            <Box key={c.key} sx={{ display: 'flex', alignItems: 'center', gap: 2.5, px: 3, py: 2, color: 'text.secondary' }}>
              <Box sx={{ color: c.passed ? tokens.color.success : tokens.color.warning, display: 'flex' }}>
                {c.passed ? <Check size={12} strokeWidth={2.5} /> : <TriangleAlert size={12} strokeWidth={2} />}
              </Box>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {String(c.meta?.detected_crs ?? c.rule).replace('EPSG:4326', 'WGS-84 · EPSG:4326')}
              </Typography>
              <Typography variant="caption">{c.passed ? 'passed' : 'flagged'}</Typography>
            </Box>
          ) : (
            <CheckRow
              key={c.key}
              label={c.label}
              count={c.plots}
              tone="med"
              error={c.error}
              repairable={c.repairable && c.plots > 0}
              active={activeChecks.includes(c.key)}
              onClick={() => onToggleCheck(c.key)}
            />
          ),
        )}
      </Box>
    </Box>
  )
}

function Stat({
  value, label, colour, testId,
}: { value: number; label: string; colour?: string; testId?: string }) {
  return (
    <Box>
      <Typography
        data-testid={testId}
        sx={{ font: `500 16px ${tokens.font.sans}`, color: colour ?? 'text.primary', fontVariantNumeric: 'tabular-nums' }}
      >
        {num(value)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
  )
}

function CheckRow({
  label, count, tone, active, onClick, eudr, repairable, error, notCovered = 0,
}: {
  label: string
  /** Polygons outside the reference layer's coverage, which were not judged. */
  notCovered?: number
  count: number
  tone: 'high' | 'med'
  active: boolean
  onClick: () => void
  eudr?: boolean
  repairable?: boolean
  /** The check could not run. Zero findings here is not a clean result. */
  error?: string
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={error}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2.5, px: 3, py: 2, border: 0, width: '100%',
        textAlign: 'left', cursor: 'pointer', borderRadius: `${tokens.radius.md}px`,
        backgroundColor: active ? tokens.color.ink100 : 'transparent',
        transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
        '&:hover': { backgroundColor: tokens.color.ink100 },
      }}
    >
      {error ? (
        <Box sx={{ width: 10, flex: 'none', color: tokens.color.warning, display: 'flex' }}>
          <TriangleAlert size={12} strokeWidth={2} />
        </Box>
      ) : (
        <Box
          sx={{
            width: 10, height: 10, flex: 'none', borderRadius: `${tokens.radius.xs}px`,
            backgroundColor: tone === 'high' ? tokens.color.dataRisk.high : tokens.color.dataRisk.med,
          }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ font: `${active ? 500 : 400} 13px ${tokens.font.sans}`, color: 'text.primary' }}>
          {label}
        </Typography>
        {/* Counted as neither passed nor failed: no reference data under them. */}
        {!error && notCovered > 0 && (
          <Typography variant="caption" sx={{ color: tokens.color.warning, display: 'block' }}>
            {num(notCovered)} outside layer coverage · not judged
          </Typography>
        )}
      </Box>
      {eudr && <EudrTag />}
      {!error && repairable && <RepairableTag />}
      {error ? (
        // Zero findings from a check that never ran is not a clean result,
        // and a bare 0 here reads exactly like one.
        <Typography variant="caption" sx={{ color: tokens.color.warning }}>
          not run
        </Typography>
      ) : (
        <Typography sx={{ font: `500 13px ${tokens.font.sans}`, fontVariantNumeric: 'tabular-nums' }}>
          {num(count)}
        </Typography>
      )}
    </Box>
  )
}
